import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  TextField, 
  Paper, 
  Alert,
  CircularProgress
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import { sendPasswordReset } from '../firebase';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // メールアドレス検証
  const validateEmail = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // フォームの検証
  const validateForm = () => {
    const errors = {};

    if (!email) {
      errors.email = 'メールアドレスを入力してください';
    } else if (!validateEmail()) {
      errors.email = '有効なメールアドレスを入力してください';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // パスワードリセットリクエストの送信
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // フォームの検証
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Firebase からパスワードリセットメールを送信（独自ドメイン設定に従って送信される）
      await sendPasswordReset(email);
      // セキュリティ上、メールの存在有無に関わらず成功表示にする（アカウント列挙対策）
      setSuccess(true);
      setError('');
      setEmail('');
    } catch (err) {
      console.error('Password reset request error:', err);
      // 未登録メール等でも、存在を推測させないため成功表示にする
      const code = err?.code || '';
      if (code === 'auth/invalid-email') {
        setError('有効なメールアドレスを入力してください');
      } else if (code === 'auth/too-many-requests') {
        setError('リクエストが多すぎます。しばらくしてから再度お試しください');
      } else {
        // user-not-found などは成功扱い
        setSuccess(true);
        setEmail('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    // リアルタイムでエラーをクリア
    if (formErrors.email) {
      setFormErrors({ ...formErrors, email: '' });
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, mt: 6, textAlign: 'center' }}>
          <LockResetIcon color="primary" sx={{ fontSize: 60 }} />
          <Typography variant="h5" sx={{ mt: 2 }}>
            メールを送信しました
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
            パスワードリセットのリンクを記載したメールを送信しました。
            メールボックスをご確認ください。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            メールが届かない場合は、迷惑メールフォルダもご確認ください。
          </Typography>
          <Button 
            component={Link}
            to="/login"
            variant="contained" 
            color="primary" 
            fullWidth 
            sx={{ mt: 2 }}
          >
            ログインページへ戻る
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 6 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <LockResetIcon color="primary" sx={{ fontSize: 60 }} />
          <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
            パスワードをお忘れですか？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            登録されているメールアドレスを入力してください。
            パスワードリセットのリンクをお送りします。
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2, width: '100%' }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={handleEmailChange}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  送信中...
                </>
              ) : (
                'リセットリンクを送信'
              )}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Button color="primary">
                  ログインページへ戻る
                </Button>
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default ForgotPassword; 